/**
 * Short_Code: 11 Crockford Base32 payload symbols (55 bits) + 1 mod-37 check symbol.
 *
 * Platform-neutral (Node, browser, React Native): no Node built-ins are imported.
 * Randomness comes from Web Crypto `globalThis.crypto.getRandomValues` unless an
 * RNG is injected.
 *
 * Requirements 11.1, 11.2, 11.3, 11.4.
 */
/** Crockford Base32 alphabet, values 0–31. */
export const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** Check alphabet, values 0–36: the Crockford alphabet plus `*~$=U`. */
export const CHECK_ALPHABET = `${ALPHABET}*~$=U`;
/** Number of payload symbols. */
export const PAYLOAD_LENGTH = 11;
/** Total symbols in a Short_Code (payload + check). */
export const CODE_LENGTH = PAYLOAD_LENGTH + 1;
/** Payload width in bits (11 symbols × 5 bits). */
export const PAYLOAD_BITS = 55;
/** Exclusive upper bound of a payload value (2^55). */
export const PAYLOAD_LIMIT = 1n << BigInt(PAYLOAD_BITS);
const CHECK_MODULUS = 37n;
const PAYLOAD_MASK = PAYLOAD_LIMIT - 1n;
/** Bytes drawn from the RNG for one payload (56 bits, top bit masked off). */
export const RANDOM_BYTES = 7;
const PAYLOAD_VALUES = new Map([...ALPHABET].map((ch, i) => [ch, i]));
const CHECK_VALUES = new Map([...CHECK_ALPHABET].map((ch, i) => [ch, i]));
function assertPayload(payload) {
    if (payload < 0n || payload >= PAYLOAD_LIMIT) {
        throw new RangeError(`Short_Code payload must be in [0, 2^${PAYLOAD_BITS})`);
    }
}
/** Check symbol for a payload value: `CHECK_ALPHABET[payload mod 37]`. */
export function checkSymbol(payload) {
    assertPayload(payload);
    return CHECK_ALPHABET[Number(payload % CHECK_MODULUS)];
}
/** Encode a 55-bit payload as 11 big-endian Crockford symbols plus the check symbol. */
export function encode(payload) {
    assertPayload(payload);
    const symbols = new Array(PAYLOAD_LENGTH);
    let rest = payload;
    for (let i = PAYLOAD_LENGTH - 1; i >= 0; i--) {
        symbols[i] = ALPHABET[Number(rest & 31n)];
        rest >>= 5n;
    }
    return (symbols.join('') + checkSymbol(payload));
}
/**
 * Normalise user input: uppercase, strip `-` and whitespace, map `I`/`L` → `1`
 * and `O` → `0`. `U` is left alone: it is only valid as the check symbol, so a
 * `U` in a payload position fails `parse` with a `format` error.
 */
export function normalise(input) {
    return input
        .toUpperCase()
        .replace(/[\s-]+/g, '')
        .replace(/[IL]/g, '1')
        .replace(/O/g, '0');
}
/**
 * Parse and validate a Short_Code. Purely local: a check failure is reported
 * before any repository/database lookup (Req 11.3).
 */
export function parse(input) {
    const s = normalise(input);
    if (s.length !== CODE_LENGTH)
        return { ok: false, error: 'format' };
    let payload = 0n;
    for (let i = 0; i < PAYLOAD_LENGTH; i++) {
        const v = PAYLOAD_VALUES.get(s[i]);
        if (v === undefined)
            return { ok: false, error: 'format' };
        payload = (payload << 5n) | BigInt(v);
    }
    const check = CHECK_VALUES.get(s[PAYLOAD_LENGTH]);
    if (check === undefined)
        return { ok: false, error: 'format' };
    if (BigInt(check) !== payload % CHECK_MODULUS)
        return { ok: false, error: 'check' };
    return { ok: true, code: s, payload };
}
/** Display form `XXXX-XXXX-XXXX` (Req 11.4). Accepts any input that parses. */
export function format(code) {
    const parsed = parse(code);
    if (!parsed.ok)
        throw new TypeError(`Invalid Short_Code (${parsed.error})`);
    const c = parsed.code;
    return `${c.slice(0, 4)}-${c.slice(4, 8)}-${c.slice(8, 12)}`;
}
/** Default RNG: Web Crypto, available in Node ≥ 19, browsers and RN (with a polyfill). */
export const webCryptoRandomBytes = (buffer) => {
    const cryptoObj = globalThis.crypto;
    if (typeof cryptoObj?.getRandomValues !== 'function') {
        throw new Error('Secure random source unavailable: globalThis.crypto.getRandomValues');
    }
    cryptoObj.getRandomValues(buffer);
};
/** Convert 7 random bytes into a 55-bit payload (big-endian, top bit masked). */
export function payloadFromBytes(bytes) {
    if (bytes.length !== RANDOM_BYTES) {
        throw new RangeError(`Expected ${RANDOM_BYTES} random bytes`);
    }
    let value = 0n;
    for (const b of bytes)
        value = (value << 8n) | BigInt(b);
    return value & PAYLOAD_MASK;
}
/** Generate a new random Short_Code from 7 secure random bytes (Req 11.1). */
export function generate(randomBytes = webCryptoRandomBytes) {
    const buf = new Uint8Array(RANDOM_BYTES);
    randomBytes(buf);
    return encode(payloadFromBytes(buf));
}
//# sourceMappingURL=shortcode.js.map